import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Compass, FilmSlate, FlagPennant, Newspaper, Suitcase, X } from "@phosphor-icons/react";
import { conciergeApi, messagesApi, postsApi, reelsApi, reviewsApi, toAbsolute, travelDateApi, tripsApi } from "@/lib/apiClient";
import { toast } from "sonner";

const TABS = [
    { key: "post", label: "Posts", icon: Newspaper },
    { key: "reel", label: "Reels", icon: FilmSlate },
    { key: "review", label: "Reviews", icon: FlagPennant },
    { key: "trip", label: "Trips", icon: Suitcase },
    { key: "traveldate", label: "TravelDates", icon: Compass },
];

export default function SharePassportModal({ open, onClose, user, onSelect }) {
    const [tab, setTab] = useState("post");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        let active = true;
        setLoading(true);
        const loaders = {
            post: async () => {
                try {
                    const mine = await postsApi.mine();
                    if (Array.isArray(mine) && mine.length) return mine;
                    const feed = await postsApi.list({ limit: 20 });
                    return Array.isArray(feed?.items) ? feed.items : (Array.isArray(feed) ? feed : []);
                } catch {
                    return [];
                }
            },
            reel: async () => {
                try {
                    if (user?.handle) {
                        const myReels = await reelsApi.list({ creator: user.handle, limit: 30 });
                        if (myReels?.items?.length) return myReels.items;
                    }
                    const general = await reelsApi.list({ mode: "for_you", limit: 20 });
                    return general?.items || [];
                } catch {
                    return [];
                }
            },
            review: () => reviewsApi.mine().catch(() => []),
            trip: async () => {
                const [conciergeTrips, manualTrips] = await Promise.all([
                    conciergeApi.listTrips().catch(() => []),
                    tripsApi.list().catch(() => []),
                ]);
                const seen = new Set();
                return [...(Array.isArray(conciergeTrips) ? conciergeTrips : []), ...(Array.isArray(manualTrips) ? manualTrips : [])].filter((item) => {
                    if (!item?.id || seen.has(item.id)) return false;
                    seen.add(item.id);
                    return true;
                });
            },
            traveldate: async () => {
                try {
                    const buckets = await travelDateApi.history();
                    return [...(buckets?.planned || []), ...(buckets?.completed || []), ...(buckets?.favorites || [])].filter(
                        (item, index, arr) => arr.findIndex((entry) => entry.id === item.id) === index
                    );
                } catch {
                    return [];
                }
            },
        };
        loaders[tab]()
            .then((res) => { if (active) setItems(Array.isArray(res) ? res : []); })
            .catch(() => { if (active) setItems([]); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [open, tab, user?.handle, user?.id]);

    const emptyLabel = useMemo(() => {
        if (tab === "reel") return "No reels found to share.";
        if (tab === "traveldate") return "No saved TravelDates yet.";
        return `No ${tab}s available yet.`;
    }, [tab]);

    const choose = async (entity_type, entity_id) => {
        try {
            const preview = await messagesApi.sharedPreview({ entity_type, entity_id });
            onSelect?.({ entity_type, entity_id }, preview);
            onClose?.();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "That Passport item can’t be shared right now.");
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[96] flex items-center justify-center p-4 md:p-6"
                    style={{ background: "rgba(5,10,17,0.9)", backdropFilter: "blur(10px)" }}
                    onClick={onClose}
                    data-testid="share-passport-modal"
                >
                    <motion.div
                        initial={{ y: 18, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 14, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-3xl max-h-[85vh] flex flex-col rounded-[32px] p-6 md:p-8"
                        style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.22)", boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}
                    >
                        {/* Pinned Header */}
                        <div className="flex items-start justify-between gap-4 mb-5 shrink-0">
                            <div>
                                <div className="overline mb-1.5">Passport share</div>
                                <h3 className="font-serif text-2xl md:text-3xl text-white">Send a Passport card</h3>
                                <p className="text-xs md:text-sm text-white/55 mt-1">Share canonical Passport content without copying the underlying data.</p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-10 h-10 rounded-full glass grid place-items-center shrink-0 hover:bg-white/10 transition-colors"
                                data-testid="share-passport-close"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* Pinned Tab Bar */}
                        <div className="flex gap-2 flex-wrap mb-4 shrink-0" data-testid="share-passport-tabs">
                            {TABS.map((item) => {
                                const Icon = item.icon;
                                const active = tab === item.key;
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        onClick={() => setTab(item.key)}
                                        className="px-4 py-2 rounded-full text-xs md:text-sm inline-flex items-center gap-2 transition-all"
                                        style={{
                                            background: active ? "var(--gold)" : "rgba(255,255,255,0.04)",
                                            color: active ? "var(--navy)" : "white",
                                            border: `1px solid ${active ? "rgba(212,175,55,0.5)" : "rgba(255,255,255,0.08)"}`,
                                            fontWeight: active ? "600" : "400",
                                        }}
                                        data-testid={`share-passport-tab-${item.key}`}
                                    >
                                        <Icon size={14} /> {item.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Scrollable Items Container */}
                        <div
                            className="flex-1 min-h-0 overflow-y-auto rounded-3xl border"
                            style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.01)" }}
                            data-testid="share-passport-list"
                        >
                            {loading ? (
                                <div className="px-6 py-16 text-center text-white/45 text-sm">Loading Passport items…</div>
                            ) : items.length === 0 ? (
                                <div className="px-6 py-16 text-center text-white/45 text-sm">{emptyLabel}</div>
                            ) : items.map((item) => {
                                const title = item.caption || item.title || item.place_name || item.location_name || item.destination || item.country || item.city || item.handle || item.id;
                                const subtitle = item.user_handle ? `@${item.user_handle}` : item.city || item.country || item.status || item.tier || item.vibe;
                                const rawMedia = item.media?.[0];
                                const isVideo = tab === "reel" || rawMedia?.kind === "video" || (item.media_url && !item.poster_url && !rawMedia?.thumbnail_url);
                                const poster = item.poster_url || rawMedia?.thumbnail_url || null;
                                const mediaUrl = item.media_url || rawMedia?.url || null;
                                const imageUrl = poster || item.hero_image_url || item.image_url || (item.photos || [])[0] || (!isVideo ? mediaUrl : null) || item.avatar_url || null;

                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => choose(tab, item.id)}
                                        className="w-full px-5 py-4 text-left flex items-center gap-4 border-b last:border-b-0 hover:bg-white/5 transition-colors"
                                        style={{ borderColor: "rgba(255,255,255,0.04)" }}
                                        data-testid={`share-passport-item-${tab}-${item.id}`}
                                    >
                                        <div
                                            className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 relative bg-white/[0.04]"
                                            style={{ border: "1px solid rgba(255,255,255,0.08)" }}
                                        >
                                            {isVideo && mediaUrl ? (
                                                <video
                                                    src={toAbsolute(mediaUrl)}
                                                    poster={poster ? toAbsolute(poster) : undefined}
                                                    muted
                                                    playsInline
                                                    preload="metadata"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : imageUrl ? (
                                                <img
                                                    src={toAbsolute(imageUrl)}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = "none";
                                                        const placeholder = e.currentTarget.parentElement?.querySelector(".fallback-placeholder");
                                                        if (placeholder) placeholder.style.display = "grid";
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className="fallback-placeholder w-full h-full place-items-center text-white/35 text-[11px] font-medium"
                                                style={{ display: (isVideo && mediaUrl) || imageUrl ? "none" : "grid" }}
                                            >
                                                {tab.toUpperCase()}
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-white line-clamp-2">{title}</div>
                                            {subtitle && <div className="text-xs text-white/50 mt-1 line-clamp-1">{subtitle}</div>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
